import { useCallback, useMemo, useState } from 'react';

import { AxiosResponse } from 'axios';

import { DETAILS_DATA_TYPES } from 'store/details/utils/constants.ts';
import isBtvProgrammesResponse from 'store/details/utils/isBtvProgrammesResponse.ts';
import {
    BTVProgrammesResponse,
    RBProgram,
    ResponseError,
    VodResponse,
} from 'store/railsBuilder/reducer.types.ts';

import { noSeasonName } from '../constants.ts';
import { EpisodesState } from '../types.ts';
import requestNoSeasonEpisodesData from '../utils/requestNoSeasonEpisodesData.ts';
import requestSeasonEpisodesData from '../utils/requestSeasonEpisodesData.ts';

type Params = {
    seriesId: string;
    channelId: string | undefined;
    initialSeason: number | typeof noSeasonName;
    selectedSeasonNumber: number | typeof noSeasonName;
};

const EPISODES_LIMIT = 10;

function useProgramEpisodes({
    seriesId,
    channelId,
    initialSeason,
    selectedSeasonNumber,
}: Params) {
    const [programsState, setProgramsState] = useState<
        EpisodesState<RBProgram>
    >({ [initialSeason]: { episodes: [], page: 1, maxPages: undefined } });

    const episodes = useMemo(
        () => programsState[selectedSeasonNumber]?.episodes || [],
        [programsState, selectedSeasonNumber],
    );

    const handleLoadBTVProgramSeasonEpisodes = useCallback(
        async (
            _seriesId: string,
            _channelId: string | undefined,
            _seasonNumber: typeof initialSeason,
            _page: number,
        ) => {
            let episodesBySeason:
                | AxiosResponse<
                      BTVProgrammesResponse | VodResponse | ResponseError
                  >
                | undefined;

            if (_seasonNumber === noSeasonName) {
                episodesBySeason = await requestNoSeasonEpisodesData(
                    DETAILS_DATA_TYPES.PROGRAM,
                    _seriesId,
                    _channelId,
                    _page,
                    EPISODES_LIMIT,
                );
            } else {
                episodesBySeason = await requestSeasonEpisodesData(
                    DETAILS_DATA_TYPES.PROGRAM,
                    _seasonNumber,
                    _seriesId,
                    _channelId,
                    _page,
                    EPISODES_LIMIT,
                );
            }

            if (isBtvProgrammesResponse(episodesBySeason)) {
                setProgramsState((prevState) => ({
                    ...prevState,
                    [_seasonNumber]: {
                        ...prevState[_seasonNumber],
                        episodes:
                            _page === 1
                                ? episodesBySeason.data.programmes
                                : [
                                      ...(prevState[_seasonNumber]?.episodes ||
                                          []),
                                      ...episodesBySeason.data.programmes,
                                  ],
                        page: _page,
                        maxPages:
                            _page === 1
                                ? Math.ceil(
                                      episodesBySeason.data.total_records /
                                          EPISODES_LIMIT,
                                  )
                                : prevState[_seasonNumber]?.maxPages,
                    },
                }));
            }
        },
        [],
    );

    const handleLoadNextEpisodes = useCallback(async () => {
        const { page, maxPages } = programsState[selectedSeasonNumber] || {};
        const nextPage = page + 1;

        if (maxPages != null && nextPage <= maxPages) {
            await handleLoadBTVProgramSeasonEpisodes(
                seriesId,
                channelId,
                selectedSeasonNumber,
                nextPage,
            );
        }
    }, [
        channelId,
        handleLoadBTVProgramSeasonEpisodes,
        programsState,
        selectedSeasonNumber,
        seriesId,
    ]);

    return {
        episodes,
        onLoadEpisodes: handleLoadBTVProgramSeasonEpisodes,
        onLoadNextEpisodes: handleLoadNextEpisodes,
    };
}

export default useProgramEpisodes;
